import {
  USERS_MGMT_FETCH,
  USERS_MGMT_INVITE_USERS,
  USERS_MGMT_CREATE_USER,
  USERS_MGMT_UPDATE_USER,
  USERS_MGMT_DELETE_USER,
  USERS_MGMT_BULK_DELETE,
  USERS_MGMT_FETCH_STATS,
  USERS_MGMT_CREATE_USERS_IN_BULK,
  USERS_MGMT_UPDATE_USERS_IN_BULK,
  USERS_MGMT_ADD_RESOURCE_TO_USER,
  AUTH_ADD_RESOURCE_TO_USER,
  AUTH_REMOVE_RESOURCE_FROM_USER,
  AUTH_UPDATE_USER,
  USERS_MGMT_REMOVE_RESOURCE_FROM_USER,
  USERS_MGMT_MANAGE_RESTRICTED_WORKSPACES_ACCESS,
} from '.';
import userServiceApi from '../../api/user.service.api';
import _ from 'lodash';
import { applicationRefreshDate } from './application.actions';
import workspaceServiceApi from '../../api/workspace.service.api';
import { queryKeys } from '../../api/query-client-registry';
import { invalidateQueryCache } from './query-cache.actions';

export const fetchUsers = () => async dispatch => {
  const { data, error } = await userServiceApi.getUsers();

  if (error) {
    console.log('Failed to fetch users');
    return { error: error.message };
  }

  const { users } = data;

  dispatch({
    type: USERS_MGMT_FETCH,
    payload: { users },
  });
  return { users };
};

export const inviteUsers =
  ({ emails }) =>
  async dispatch => {
    const { error, data } = await userServiceApi.inviteUsers({ emails });

    if (error) {
      console.log('Failed to invite users');
      return { error: data.message };
    }

    dispatch({
      type: USERS_MGMT_INVITE_USERS,
      payload: data,
    });
  };

export const createUsers =
  ({ userData }) =>
  async dispatch => {
    //check if there is more than one user to create
    const isMultipleUsers = userData?.email?.length > 1;

    if (isMultipleUsers) {
      return dispatch(createUsersInBulk({ users: userData }));
    }

    const [email] = userData.email;

    const {
      user = null,
      error = null,
      isNew,
    } = await dispatch(createSingleUser({ user: { ...userData, email } }));

    return { users: user ? [user] : null, errors: error, isNew: isNew };
  };

export const createSingleUser =
  ({ user }) =>
  async dispatch => {
    const { error, data } = await userServiceApi.createUser({ user });

    if (error) {
      console.log('Failed to create user');
      return {
        error: data.message.includes('already exists')
          ? { alreadyExists: [user.email] }
          : data.message,
      };
    }

    dispatch({
      type: USERS_MGMT_CREATE_USER,
      payload: data,
    });
    // Profile-field usage counts (org-structure) are React Query — refresh them.
    dispatch(invalidateQueryCache(queryKeys.userGroupStats));
    return data;
  };

export const createUsersInBulk =
  ({ users }) =>
  async dispatch => {
    const { error, data } = await userServiceApi.createUserInBulk({ users });

    if (error) {
      console.log('Failed to create users');
      return { error: data.message };
    }

    dispatch({
      type: USERS_MGMT_CREATE_USERS_IN_BULK,
      payload: data,
    });
    dispatch(invalidateQueryCache(queryKeys.userGroupStats));
    return data;
  };

export const updateUser =
  ({ id, userData, altOwnerId }) =>
  async dispatch => {
    //check if there is more than one user to update
    const isMultipleUsers = _.isArray(userData.id) && userData?.id?.length > 1;

    if (isMultipleUsers) {
      const users = userData.id.map((id, i) => ({ ...userData, id, email: userData.email[i] }));
      return dispatch(updateUsersInBulk({ users }));
    }

    const [email] = userData.email;

    const {
      user = null,
      error = null,
      errorCode = null,
    } = await dispatch(
      updateSingleUser({
        id,
        user: {
          ...userData,
          email,
        },
        altOwnerId,
      }),
    );

    return { users: user ? [user] : null, errors: error, errorCode };
  };

export const updateSingleUser =
  ({ id, user, altOwnerId }) =>
  async (dispatch, getState) => {
    const { data, error } = await userServiceApi.updateUser({ id, user, altOwnerId });

    if (error) {
      console.log('Failed to update user data');
      // `errorCode` lets the form tell a field conflict (e.g. PROVIDER_IDENTIFICATION_TAKEN) apart.
      return { error: data?.message, errorCode: data?.code ?? null };
    }

    dispatch({
      type: USERS_MGMT_UPDATE_USER,
      payload: { id, user: data?.user },
    });

    // When an admin edits their own record, mirror it into the profile (auth.user)
    // so the profile page reflects the latest values without a refetch.
    if (data?.user && id === getState().auth?.user?.id) {
      dispatch({
        type: AUTH_UPDATE_USER,
        payload: { user: data.user },
      });
    }

    // Profile fields may have changed — refresh the org-structure usage counts.
    dispatch(invalidateQueryCache(queryKeys.userGroupStats));
    // A user change (e.g. deactivation) can shift their membership in any
    // rotation's trainee list, which is server-derived — refetch it.
    dispatch(invalidateQueryCache(queryKeys.rotationsTrainees));
    // A provider name attributes orphan records server-side: metrics and the unmatched list move.
    if (user?.providerIdentifications !== undefined) {
      dispatch(invalidateQueryCache(queryKeys.qualityMetricsAll));
    }

    if (altOwnerId) {
      dispatch(applicationRefreshDate());
    }

    return data;
  };

export const addResourceToUser =
  ({ userId, payload }) =>
  async (dispatch, getState) => {
    const { error, data } = await userServiceApi.addResourceToUser({ userId, payload });
    const state = getState();

    if (error) {
      console.log('Failed to add resource to user');
      return { error: data.message };
    }

    if (userId === state.auth?.user?.id) {
      await dispatch({
        type: AUTH_ADD_RESOURCE_TO_USER,
        payload: { resource: data },
      });
    }

    await dispatch({
      type: USERS_MGMT_ADD_RESOURCE_TO_USER,
      payload: { userId, resource: data },
    });

    return data;
  };

export const removeResourceFromUser =
  ({ userId, resourceId }) =>
  async (dispatch, getState) => {
    const { error, data } = await userServiceApi.removeResourceFromUser({ userId, id: resourceId });
    const state = getState();

    if (error) {
      console.log('Failed to remove resource from user');
      return { error: data.message };
    }

    if (userId === state.auth?.user?.id) {
      await dispatch({
        type: AUTH_REMOVE_RESOURCE_FROM_USER,
        payload: { resourceId },
      });
    }

    await dispatch({
      type: USERS_MGMT_REMOVE_RESOURCE_FROM_USER,
      payload: { userId, resourceId },
    });

    return data;
  };

export const updateUsersInBulk =
  ({ users }) =>
  async dispatch => {
    const res = await Promise.all(
      users.map(({ id, ...userData }) => userServiceApi.updateUser({ id, user: userData })),
    );

    const failures = res.filter(({ error, data }) => error || data?.error);

    const updatedUsers = res.map(({ data }) => data?.user).filter(Boolean);

    dispatch({
      type: USERS_MGMT_UPDATE_USERS_IN_BULK,
      payload: { users: updatedUsers },
    });
    dispatch(invalidateQueryCache(queryKeys.userGroupStats));
    dispatch(invalidateQueryCache(queryKeys.rotationsTrainees));

    return {
      users: updatedUsers,
      errors:
        failures.length > 0 ? failures.map(({ data }) => data?.message).filter(Boolean) : null,
    };
  };

export const deleteUser =
  ({ id, ownerId }) =>
  async dispatch => {
    const { error, data } = await userServiceApi.deleteUser({ id, ownerId });

    if (error) {
      console.log('Failed to delete user');
      return { error: data.message };
    }

    dispatch({
      type: USERS_MGMT_DELETE_USER,
      payload: { id },
    });
    dispatch(invalidateQueryCache(queryKeys.userGroupStats));
    // A deleted user can drop out of any rotation role (trainee/staff/contact/
    // owner) or timeslot, so invalidate the whole rotations domain.
    dispatch(invalidateQueryCache(queryKeys.rotationsAll));

    if (ownerId) {
      dispatch(applicationRefreshDate());
    }
  };

export const bulkDeleteUsers =
  ({ userIds, ownerId }) =>
  async dispatch => {
    const { error, data } = await userServiceApi.bulkDeleteUsers({ userIds, ownerId });

    if (error) {
      console.log('Failed to bulk delete users');
      return { error: data.message };
    }

    dispatch({
      type: USERS_MGMT_BULK_DELETE,
      payload: { userIds },
    });
    dispatch(invalidateQueryCache(queryKeys.userGroupStats));
    dispatch(invalidateQueryCache(queryKeys.rotationsAll));

    if (ownerId) {
      dispatch(applicationRefreshDate());
    }

    return data;
  };

export const fetchUserStats = () => async dispatch => {
  const { data, error } = await userServiceApi.getStats();

  if (error) {
    console.log('Failed to fetch user stats');
    return { error: error.message };
  }

  const { stats } = data;

  dispatch({
    type: USERS_MGMT_FETCH_STATS,
    payload: { stats },
  });
};

export const manageRestrictedWorkspacesAccess =
  ({ userIds: initialUserIds, workspaceIds }) =>
  async dispatch => {
    const userIds = Array.isArray(initialUserIds) ? initialUserIds : [initialUserIds];

    const { error, data } = await workspaceServiceApi.manageRestrictedWorkspacesAccess({
      userIds,
      workspaceIds,
    });

    if (error) {
      console.log('Failed to manage restricted workspaces access');
      return { error: data.message };
    }

    dispatch({
      type: USERS_MGMT_MANAGE_RESTRICTED_WORKSPACES_ACCESS,
    });
  };

const dispatchAvatarUpdated = (dispatch, getState, user) => {
  if (!user) return;
  const avatar = { id: user.id, avatarPublicId: user.avatarPublicId };
  dispatch({ type: USERS_MGMT_UPDATE_USER, payload: { id: user.id, user: avatar } });
  if (getState().auth?.user?.id === user.id) {
    dispatch({ type: AUTH_UPDATE_USER, payload: { user: avatar } });
  }
};

export const updateAvatar =
  ({ userId, file }) =>
  async (dispatch, getState) => {
    const { data, error } = await userServiceApi.uploadAvatar({ userId, file });
    const avatarPublicId = data?.user?.avatarPublicId;
    // Success requires a new avatar id back, not just a falsy `error`: a network/client
    // failure resolves to { data: {}, error } where data.message is undefined, which would
    // otherwise surface as { error: undefined } and read as success to the caller.
    if (error || !avatarPublicId) {
      return { error: data?.message ?? error ?? 'Avatar upload failed' };
    }
    dispatchAvatarUpdated(dispatch, getState, data?.user);
    return { data };
  };

export const deleteAvatar =
  ({ userId }) =>
  async (dispatch, getState) => {
    const { data, error } = await userServiceApi.deleteAvatar({ userId });
    // Mirror of updateAvatar: success means the avatar id is gone. A network/client failure
    // resolves to { data: {}, error }, which would otherwise read as success to the caller.
    if (error || data?.user?.avatarPublicId) {
      return { error: data?.message ?? error ?? 'Avatar delete failed' };
    }
    dispatchAvatarUpdated(dispatch, getState, data?.user);
    return { data };
  };
